import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './CashflowPage.css';
import {
    CashSessionDTO,
    CashflowGranularity,
    CashflowRecordDTO,
    CashflowSummaryBucket,
    CashflowSummaryResponse,
    closeCashSession,
    fetchCashflowRecords,
    fetchCashflowSummary,
    listCashSessions,
    openCashSession,
    voidCashflowPayment,
    exportCashflowRecords,
} from '../../api/cashflowApi';
import { CashflowTrendChart, CashflowDistributionChart } from './CashflowCharts';

const pageSize = 25;

const granularityOptions: { value: CashflowGranularity; label: string }[] = [
    { value: 'DAY', label: 'Giornaliero' },
    { value: 'WEEK', label: 'Settimanale' },
    { value: 'MONTH', label: 'Mensile' },
    { value: 'SESSION', label: 'Sessione cassa' },
];

const channelLabels: Record<string, string> = {
    DELIVERY: 'Delivery',
    TAKEAWAY: 'Take-away',
    DINE_IN: 'Sala',
};

const methodLabels: Record<string, string> = {
    POS: 'POS',
    CASH: 'Contanti',
    ONLINE: 'Online',
    OTHER: 'Altro',
};

const operatorFallbackLabels: Record<string, string> = {
    UNASSIGNED: 'Non assegnato',
    UNKNOWN: 'Sconosciuto',
};

type FiltersState = {
    from: string;
    to: string;
    granularity: CashflowGranularity;
    channel: string;
    method: string;
    operator: string;
};

function getDefaultDateRange(): { from: string; to: string } {
    const now = new Date();
    const to = now.toISOString();
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - 7);
    return { from: fromDate.toISOString(), to };
}

function toDateInputValue(value: string): string {
    return value ? value.substring(0, 10) : '';
}

function alignDateToIso(dateValue: string, endOfDay = false): string {
    if (!dateValue) {
        return '';
    }
    const target = new Date(dateValue);
    if (Number.isNaN(target.getTime())) {
        return '';
    }
    if (endOfDay) {
        target.setHours(23, 59, 59, 999);
    } else {
        target.setHours(0, 0, 0, 0);
    }
    return target.toISOString();
}

function toNumeric(value?: number | string | null): number {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

function formatCurrency(amount?: number | string | null): string {
    const numeric = toNumeric(amount);
    if (!Number.isFinite(numeric)) {
        return '€ 0,00';
    }
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(numeric);
}

function formatDateTime(value?: string | null): string {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function bucketLabel(bucket: CashflowSummaryBucket): string {
    if (bucket.label) {
        return bucket.label;
    }
    if (bucket.cashSessionId) {
        return `Sessione #${bucket.cashSessionId}`;
    }
    const start = bucket.periodStart ? formatDateTime(bucket.periodStart) : '';
    const end = bucket.periodEnd ? formatDateTime(bucket.periodEnd) : '';
    if (start && end && start !== end) {
        return `${start} → ${end}`;
    }
    return start || end || 'Periodo';
}

function formatOperatorLabel(value?: string | null): string {
    if (!value) {
        return operatorFallbackLabels.UNASSIGNED;
    }
    return operatorFallbackLabels[value] || value;
}

function hasEntries(map?: Record<string, number> | null): map is Record<string, number> {
    return Boolean(map && Object.keys(map).length > 0);
}

const CashflowPage: React.FC = () => {
    const { from, to } = useMemo(getDefaultDateRange, []);
    const [filters, setFilters] = useState<FiltersState>({
        from,
        to,
        granularity: 'DAY',
        channel: 'ALL',
        method: 'ALL',
        operator: 'ALL',
    });
    const [page, setPage] = useState(0);

    const [summary, setSummary] = useState<CashflowSummaryResponse | null>(null);
    const [summaryError, setSummaryError] = useState<string | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    const [records, setRecords] = useState<CashflowRecordDTO[]>([]);
    const [recordsMeta, setRecordsMeta] = useState<{ totalCount: number; totalAmount: number }>({ totalCount: 0, totalAmount: 0 });
    const [recordsError, setRecordsError] = useState<string | null>(null);
    const [recordsLoading, setRecordsLoading] = useState(false);

    const [sessions, setSessions] = useState<CashSessionDTO[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [sessionsError, setSessionsError] = useState<string | null>(null);

    const [actionMessage, setActionMessage] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    const activeSession = useMemo(() => sessions.find(session => session.open), [sessions]);

    const operatorOptions = useMemo(() => {
        const candidates = new Set<string>();
        records.forEach(record => {
            if (record.recordedBy && record.recordedBy.trim().length > 0) {
                candidates.add(record.recordedBy.trim());
            }
        });
        if (summary?.amountByOperator) {
            Object.keys(summary.amountByOperator)
                .filter(key => key && !['UNASSIGNED', 'UNKNOWN'].includes(key))
                .forEach(key => candidates.add(key));
        }
        sessions.forEach(session => {
            if (session.openedBy && session.openedBy.trim().length > 0) {
                candidates.add(session.openedBy.trim());
            }
            if (session.closedBy && session.closedBy.trim().length > 0) {
                candidates.add(session.closedBy.trim());
            }
        });
        return Array.from(candidates).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
    }, [records, sessions, summary?.amountByOperator]);

    const summaryBreakdownSections = useMemo(() => {
        if (!summary) {
            return [] as Array<{ title: string; entries: Array<{ key: string; label: string; value: number }> }>;
        }
        const sources = [
            {
                title: 'Metodi di pagamento',
                map: summary.amountByMethod ?? {},
                labeler: (key: string) => methodLabels[key] || key,
            },
            {
                title: 'Canali di vendita',
                map: summary.amountByChannel ?? {},
                labeler: (key: string) => channelLabels[key] || key,
            },
            {
                title: 'Operatori',
                map: summary.amountByOperator ?? {},
                labeler: (key: string) => formatOperatorLabel(key),
            },
        ];
        return sources.map(section => {
            const entries = Object.entries(section.map)
                .map(([key, value]) => ({ key, label: section.labeler(key), value: toNumeric(value) }))
                .filter(entry => entry.value > 0)
                .sort((a, b) => b.value - a.value);
            return { title: section.title, entries };
        }).filter(section => section.entries.length > 0);
    }, [summary]);

    const trendData = useMemo(() => {
        if (!summary?.buckets || summary.buckets.length === 0) {
            return [] as Array<{ label: string; value: number }>;
        }
        return summary.buckets.map(bucket => ({
            label: bucketLabel(bucket),
            value: toNumeric(bucket.totalAmount),
        }));
    }, [summary]);

    const distributionData = useMemo(() => {
        if (!summary?.amountByMethod) {
            return [] as Array<{ label: string; value: number }>;
        }
        return Object.entries(summary.amountByMethod)
            .map(([key, value]) => ({ label: methodLabels[key] || key, value: toNumeric(value) }))
            .filter(entry => entry.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [summary]);

    const renderBucketBreakdown = useCallback((title: string, map?: Record<string, number> | null, labelResolver?: (key: string) => string) => {
        if (!hasEntries(map)) {
            return null;
        }
        const entries = Object.entries(map ?? {});
        return (
            <div className="bucket-breakdown" key={title}>
                <h5>{title}</h5>
                <ul>
                    {entries.map(([key, value]) => (
                        <li key={key}>
                            <span>{labelResolver ? labelResolver(key) : key}</span>
                            <strong>{formatCurrency(value)}</strong>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }, []);

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            const blob = await exportCashflowRecords({
                from: filters.from,
                to: filters.to,
                channel: filters.channel === 'ALL' ? undefined : filters.channel,
                method: filters.method === 'ALL' ? undefined : filters.method,
                operator: filters.operator === 'ALL' ? undefined : filters.operator,
            });
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `cashflow-records-${timestamp}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            setActionMessage('Esportazione completata. Il file è stato scaricato.');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Errore durante l\'esportazione dei movimenti.';
            setActionMessage(message);
        } finally {
            setExporting(false);
        }
    }, [filters.channel, filters.from, filters.method, filters.operator, filters.to]);

    const loadSummary = useCallback(async () => {
        setSummaryLoading(true);
        setSummaryError(null);
        try {
            const response = await fetchCashflowSummary({
                from: filters.from,
                to: filters.to,
                granularity: filters.granularity,
                channel: filters.channel === 'ALL' ? undefined : filters.channel,
                method: filters.method === 'ALL' ? undefined : filters.method,
                operator: filters.operator === 'ALL' ? undefined : filters.operator,
            });
            setSummary(response);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Errore nel recupero del riepilogo.';
            setSummaryError(message);
        } finally {
            setSummaryLoading(false);
        }
    }, [filters.channel, filters.from, filters.granularity, filters.method, filters.operator, filters.to]);

    const loadRecords = useCallback(async (targetPage = page) => {
        setRecordsLoading(true);
        setRecordsError(null);
        try {
            const response = await fetchCashflowRecords({
                from: filters.from,
                to: filters.to,
                channel: filters.channel === 'ALL' ? undefined : filters.channel,
                method: filters.method === 'ALL' ? undefined : filters.method,
                operator: filters.operator === 'ALL' ? undefined : filters.operator,
                page: targetPage,
                size: pageSize,
            });
            setRecords(response.records);
            setRecordsMeta({ totalCount: response.totalCount, totalAmount: toNumeric(response.totalAmount) });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Errore nel recupero dei movimenti di cassa.';
            setRecordsError(message);
        } finally {
            setRecordsLoading(false);
        }
    }, [filters.channel, filters.from, filters.method, filters.operator, filters.to, page]);

    const loadSessions = useCallback(async () => {
        setSessionsLoading(true);
        setSessionsError(null);
        try {
            const response = await listCashSessions();
            setSessions(response);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Errore nel recupero delle sessioni di cassa.';
            setSessionsError(message);
        } finally {
            setSessionsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    useEffect(() => {
        loadRecords();
    }, [loadRecords]);

    useEffect(() => {
        loadSessions();
    }, [loadSessions]);

    const handleFiltersChange = (next: Partial<FiltersState>) => {
        setPage(0);
        setFilters(prev => ({ ...prev, ...next }));
    };

    const totalPages = useMemo(() => {
        return recordsMeta.totalCount > 0 ? Math.ceil(recordsMeta.totalCount / pageSize) : 0;
    }, [recordsMeta.totalCount]);

    const handleOpenSession = async () => {
        const value = window.prompt('Importo fondo cassa iniziale (opzionale):', activeSession?.openingFloat ? String(activeSession.openingFloat) : '');
        if (value === null) {
            return;
        }
        let openingFloat: number | undefined;
        if (value.trim().length > 0) {
            const parsed = Number(value.replace(',', '.'));
            if (!Number.isFinite(parsed)) {
                setActionMessage('Valore non valido per il fondo cassa. Operazione annullata.');
                return;
            }
            openingFloat = Number(parsed.toFixed(2));
        }
        try {
            await openCashSession(openingFloat !== undefined ? { openingFloat } : {});
            setActionMessage('Sessione cassa aperta correttamente.');
            await Promise.all([loadSessions(), loadSummary(), loadRecords(page)]);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile aprire la sessione di cassa.';
            setActionMessage(message);
        }
    };

    const handleCloseSession = async () => {
        if (!activeSession) {
            return;
        }
        const closingValue = window.prompt('Totale contante presente in cassa?', '');
        if (closingValue === null) {
            return;
        }
        const note = window.prompt('Note di chiusura (facoltative):', '') || undefined;
        let closingCash: number | undefined;
        if (closingValue.trim().length > 0) {
            const parsed = Number(closingValue.replace(',', '.'));
            if (!Number.isFinite(parsed)) {
                setActionMessage('Valore non valido per il totale contante. Operazione annullata.');
                return;
            }
            closingCash = Number(parsed.toFixed(2));
        }
        try {
            await closeCashSession(activeSession.id, { closingCash, notes: note });
            setActionMessage('Sessione cassa chiusa con successo.');
            await Promise.all([loadSessions(), loadSummary(), loadRecords(page)]);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile chiudere la sessione di cassa.';
            setActionMessage(message);
        }
    };

    const handleVoidRecord = async (record: CashflowRecordDTO) => {
        const confirmed = window.confirm(`Stornare l'incasso di ${formatCurrency(record.amount)} per l'ordine ${record.orderId || record.id}?`);
        if (!confirmed) {
            return;
        }
        const reason = window.prompt('Motivo dello storno (facoltativo):', '') || undefined;
        try {
            await voidCashflowPayment(record.id, reason ? { reason } : {});
            setActionMessage('Incasso stornato correttamente.');
            await Promise.all([loadSummary(), loadRecords(page)]);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Impossibile stornare l\'incasso.';
            setActionMessage(message);
        }
    };

    return (
        <div className="cashflow-page">
            <header className="cashflow-header">
                <div>
                    <h1>Flusso di cassa</h1>
                    <p>Monitora incassi, sessioni di cassa e attività POS registrate dal personale.</p>
                </div>
                <div className="cashflow-actions">
                    {activeSession ? (
                        <button type="button" className="danger" onClick={handleCloseSession}>
                            Chiudi sessione #{activeSession.id}
                        </button>
                    ) : (
                        <button type="button" onClick={handleOpenSession}>
                            Apri sessione cassa
                        </button>
                    )}
                </div>
            </header>

            <section className="cashflow-filters">
                <div className="filter-group">
                    <label>Dal
                        <input
                            type="date"
                            value={toDateInputValue(filters.from)}
                            onChange={(event) => handleFiltersChange({ from: alignDateToIso(event.target.value) })}
                        />
                    </label>
                    <label>Al
                        <input
                            type="date"
                            value={toDateInputValue(filters.to)}
                            onChange={(event) => handleFiltersChange({ to: alignDateToIso(event.target.value, true) })}
                        />
                    </label>
                    <label>Raggruppa per
                        <select
                            value={filters.granularity}
                            onChange={(event) => handleFiltersChange({ granularity: event.target.value as CashflowGranularity })}
                        >
                            {granularityOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                    <label>Canale
                        <select
                            value={filters.channel}
                            onChange={(event) => handleFiltersChange({ channel: event.target.value })}
                        >
                            <option value="ALL">Tutti</option>
                            {Object.entries(channelLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </label>
                    <label>Metodo
                        <select
                            value={filters.method}
                            onChange={(event) => handleFiltersChange({ method: event.target.value })}
                        >
                            <option value="ALL">Tutti</option>
                            {Object.entries(methodLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </label>
                    <label>Operatore
                        <select
                            value={filters.operator}
                            onChange={(event) => handleFiltersChange({ operator: event.target.value })}
                        >
                            <option value="ALL">Tutti</option>
                            {operatorOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </label>
                </div>
                {actionMessage && (
                    <div className="cashflow-toast" role="status">
                        {actionMessage}
                        <button type="button" onClick={() => setActionMessage(null)}>×</button>
                    </div>
                )}
            </section>

            <section className="cashflow-summary">
                <header>
                    <h2>Riepilogo</h2>
                    {summaryLoading && <span className="loader">Caricamento…</span>}
                </header>
                {summaryError && <div className="alert error">{summaryError}</div>}
                <div className="summary-grid">
                    <article>
                        <h3>Totale incassato</h3>
                        <strong>{formatCurrency(summary?.totalAmount)}</strong>
                        <span>{summary?.totalCount ?? 0} incassi · {summary?.ordersCount ?? 0} ordini</span>
                        <ul className="summary-kpi-list">
                            <li>Ticket medio incasso: <strong>{formatCurrency(summary?.averagePerPayment)}</strong></li>
                            <li>Ticket medio ordine: <strong>{formatCurrency(summary?.averagePerOrder)}</strong></li>
                            <li>IVA stimata (22%): <strong>{formatCurrency(summary?.estimatedVat)}</strong></li>
                        </ul>
                    </article>
                    <article>
                        <h3>Sessione attiva</h3>
                        {activeSession ? (
                            <>
                                <strong>#{activeSession.id}</strong>
                                <span>Aperta da {activeSession.openedBy || '—'} il {formatDateTime(activeSession.openedAt)}</span>
                                <span>{formatCurrency(activeSession.paymentsAmount ?? 0)} registrati ({activeSession.paymentsCount ?? 0} movimenti)</span>
                            </>
                        ) : (
                            <>
                                <strong>Nessuna sessione aperta</strong>
                                <span>Apri una sessione per monitorare gli incassi POS giornalieri.</span>
                            </>
                        )}
                    </article>
                </div>
                {summaryBreakdownSections.length > 0 && (
                    <div className="summary-breakdowns">
                        {summaryBreakdownSections.map(section => (
                            <section key={section.title}>
                                <h4>{section.title}</h4>
                                <ul>
                                    {section.entries.map(entry => (
                                        <li key={entry.key}>
                                            <span>{entry.label}</span>
                                            <strong>{formatCurrency(entry.value)}</strong>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                )}
                {(trendData.length > 1 || distributionData.length > 0) && (
                    <div className="summary-charts">
                        {trendData.length > 1 && (
                            <div className="chart-card">
                                <h4>Andamento incassi</h4>
                                <CashflowTrendChart data={trendData} />
                            </div>
                        )}
                        {distributionData.length > 0 && (
                            <div className="chart-card">
                                <h4>Ripartizione per metodo</h4>
                                <CashflowDistributionChart data={distributionData} />
                            </div>
                        )}
                    </div>
                )}

                <div className="buckets-grid">
                    {summary?.buckets?.length ? summary.buckets.map(bucket => (
                        <article key={`${bucket.cashSessionId ?? bucket.periodStart ?? bucket.label}`}>
                            <header>
                                <h4>{bucketLabel(bucket)}</h4>
                                <span>{bucket.paymentCount ?? 0} movimenti</span>
                            </header>
                            <strong>{formatCurrency(bucket.totalAmount)}</strong>
                            {(hasEntries(bucket.amountByMethod) || hasEntries(bucket.amountByChannel) || hasEntries(bucket.amountByOperator)) && (
                                <div className="bucket-breakdowns">
                                    {renderBucketBreakdown('Metodi', bucket.amountByMethod, method => methodLabels[method] || method)}
                                    {renderBucketBreakdown('Canali', bucket.amountByChannel, channel => channelLabels[channel] || channel)}
                                    {renderBucketBreakdown('Operatori', bucket.amountByOperator, formatOperatorLabel)}
                                </div>
                            )}
                        </article>
                    )) : (
                        <div className="empty-state">Nessun dato disponibile per il periodo selezionato.</div>
                    )}
                </div>
            </section>

            <section className="cashflow-sessions">
                <header>
                    <h2>Storico sessioni</h2>
                    {sessionsLoading && <span className="loader">Caricamento…</span>}
                </header>
                {sessionsError && <div className="alert error">{sessionsError}</div>}
                <div className="sessions-timeline">
                    {sessions.length === 0 && !sessionsLoading && (
                        <div className="empty-state">Nessuna sessione trovata.</div>
                    )}
                    {sessions.map(session => (
                        <article key={session.id} className={session.open ? 'open' : 'closed'}>
                            <header>
                                <h3>Sessione #{session.id}</h3>
                                <span>{session.open ? 'APERTA' : 'CHIUSA'}</span>
                            </header>
                            <dl>
                                <div>
                                    <dt>Aperta da</dt>
                                    <dd>{session.openedBy || '—'}</dd>
                                </div>
                                <div>
                                    <dt>Apertura</dt>
                                    <dd>{formatDateTime(session.openedAt)}</dd>
                                </div>
                                <div>
                                    <dt>Fondo iniziale</dt>
                                    <dd>{formatCurrency(session.openingFloat ?? 0)}</dd>
                                </div>
                                <div>
                                    <dt>Movimenti</dt>
                                    <dd>{session.paymentsCount ?? 0} · {formatCurrency(session.paymentsAmount ?? 0)}</dd>
                                </div>
                                <div>
                                    <dt>Chiusura</dt>
                                    <dd>{session.closedAt ? `${formatDateTime(session.closedAt)} da ${session.closedBy || '—'}` : '—'}</dd>
                                </div>
                                <div>
                                    <dt>Cassa finale</dt>
                                    <dd>{session.closedAt ? formatCurrency(session.closingCash ?? 0) : '—'}</dd>
                                </div>
                                {session.closingNotes && (
                                    <div>
                                        <dt>Note</dt>
                                        <dd>{session.closingNotes}</dd>
                                    </div>
                                )}
                            </dl>
                        </article>
                    ))}
                </div>
            </section>

            <section className="cashflow-records">
                <header>
                    <div>
                        <h2>Movimenti registrati</h2>
                        <p>{recordsMeta.totalCount} movimenti · {formatCurrency(recordsMeta.totalAmount)}</p>
                    </div>
                    <div className="records-actions">
                        {recordsLoading && <span className="loader">Caricamento…</span>}
                        <button type="button" onClick={handleExport} disabled={exporting} className="ghost">
                            {exporting ? 'Esportazione…' : 'Esporta CSV'}
                        </button>
                    </div>
                </header>
                {recordsError && <div className="alert error">{recordsError}</div>}
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Ordine</th>
                                <th>Canale</th>
                                <th>Metodo</th>
                                <th>Importo</th>
                                <th>Operatore</th>
                                <th>Sessione</th>
                                <th>Stato</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.length === 0 && !recordsLoading && (
                                <tr>
                                    <td colSpan={9} className="empty-state">Nessun incasso trovato.</td>
                                </tr>
                            )}
                            {records.map(record => {
                                const channelLabel = record.channel ? (channelLabels[record.channel] || record.channel) : '—';
                                const methodLabel = record.method ? (methodLabels[record.method] || record.method) : '—';
                                const isVoided = Boolean(record.voidedAt);
                                return (
                                    <tr key={record.id} className={isVoided ? 'voided' : ''}>
                                        <td>{formatDateTime(record.receivedAt || record.recordedAt)}</td>
                                        <td>{record.orderId || '—'}</td>
                                        <td>
                                            <span className={`table-pill table-pill__channel ${record.channel ? record.channel.toLowerCase() : 'unknown'}`}>
                                                {channelLabel}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`table-pill table-pill__method ${record.method ? record.method.toLowerCase() : 'unknown'}`}>
                                                {methodLabel}
                                            </span>
                                        </td>
                                        <td>{formatCurrency(record.amount)}</td>
                                        <td>{record.recordedBy || '—'}</td>
                                        <td>{record.cashSessionId ? `#${record.cashSessionId}` : '—'}</td>
                                        <td>
                                            <span className={`status-pill ${isVoided ? 'status-pill__voided' : 'status-pill__valid'}`}>
                                                {isVoided ? 'Stornato' : 'Valido'}
                                            </span>
                                            {isVoided && (
                                                <span className="status-note">da {record.voidedBy || '—'}</span>
                                            )}
                                        </td>
                                        <td className="actions-cell">
                                            {!isVoided && (
                                                <button type="button" className="ghost" onClick={() => handleVoidRecord(record)}>
                                                    Annulla
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="pagination">
                        <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>Precedente</button>
                        <span>Pagina {page + 1} di {totalPages}</span>
                        <button type="button" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Successiva</button>
                    </div>
                )}
            </section>
        </div>
    );
};

export default CashflowPage;
